import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import CopilotPanel from "../copilot/CopilotPanel";
import CheerModal from "../CheerModal";
import useCopilotStore from "../../store/copilotStore";
import useCheerStore from "../../store/cheerStore";
import useAuthStore from "../../store/authStore";

const COMEBACK_MS = 10 * 60 * 1000; // 10 minutes

export default function AppLayout() {
  const isOpen  = useCopilotStore((s) => s.isOpen);
  const trigger = useCheerStore((s) => s.trigger);
  const { isAuthenticated } = useAuthStore();

  const [panelWidth, setPanelWidth] = useState(384);
  const isDragging  = useRef(false);
  const dragStart   = useRef({ x: 0, width: 0 });
  const hiddenAt    = useRef(null);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = dragStart.current.x - e.clientX;
      setPanelWidth(
        Math.max(300, Math.min(window.innerWidth * 0.55, dragStart.current.width + delta))
      );
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Comeback detection — trigger a cheer if the user returns after ≥10 min away.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt.current = Date.now();
      } else {
        if (hiddenAt.current && Date.now() - hiddenAt.current >= COMEBACK_MS) {
          const mins = Math.round((Date.now() - hiddenAt.current) / 60000);
          const label = mins >= 120 ? "suspiciously long"
            : mins >= 60  ? `${Math.floor(mins / 60)}h ${mins % 60}m`
            : `${mins} min`;
          trigger("comeback", `Break duration: ${label}`);
        }
        hiddenAt.current = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isAuthenticated, trigger]);

  const onDividerMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current  = { x: e.clientX, width: panelWidth };
    document.body.style.cursor     = "col-resize";
    document.body.style.userSelect = "none";
    e.preventDefault();
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
        <Outlet />
      </div>

      {isOpen && (
        <>
          <div
            onMouseDown={onDividerMouseDown}
            className="w-1.5 shrink-0 bg-gray-200 hover:bg-bh-400 active:bg-bh-500 cursor-col-resize transition-colors"
          />
          <div className="shrink-0 flex flex-col overflow-hidden" style={{ width: panelWidth }}>
            <CopilotPanel />
          </div>
        </>
      )}

      {/* Global cheer modal — rendered here so any page can trigger it */}
      <CheerModal />
    </div>
  );
}
